/**
 * BalanceSheetTable — multi-year Balance Sheet table.
 *
 * Shows assets, liabilities, and equity across up to 10 years.
 */
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCompactCurrency, formatUnitLabel } from '../utils/formatters';
import { resolveRecordValue } from '../utils/extendedFields';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function BalanceSheetTable({ data, region }: Props) {
  const years = data;
  const unitLabel = formatUnitLabel(region);

  type RowDef = {
    label: string;
    key: keyof UnifiedFinancialRecord;
    indent?: boolean;
    bold?: boolean;
    separator?: boolean;
    sectionHeader?: boolean;
    /**
     * PDF-only extras have no first-class DTO field — resolve them through
     * every spelling they may arrive under (extended_fields carries the Neo4j
     * PascalCase names). Rows with no value in any year are dropped, so these
     * are harmless for scraped companies where the properties don't exist.
     */
    extKeys?: string[];
  };

  const rows: RowDef[] = [
    { label: 'ASSETS', key: 'total_assets', sectionHeader: true },
    { label: 'Fixed / Non-Current Assets', key: 'fixed_assets', indent: true },
    // Bare printed PPE — Fixed_Assets above is the aggregate.
    { label: 'Property, Plant & Equipment', key: 'fixed_assets', indent: true, extKeys: ['Property_Plant_Equipment', 'property_plant_equipment'] },
    { label: 'Capital Work in Progress', key: 'fixed_assets', indent: true, extKeys: ['Capital_Work_In_Progress', 'capital_work_in_progress'] },
    { label: 'Right-of-Use Assets', key: 'fixed_assets', indent: true, extKeys: ['Right_Of_Use_Assets', 'right_of_use_assets'] },
    { label: 'Investment Property', key: 'fixed_assets', indent: true, extKeys: ['Investment_Property', 'investment_property'] },
    { label: 'Goodwill', key: 'fixed_assets', indent: true, extKeys: ['Goodwill', 'goodwill'] },
    { label: 'Intangibles Under Development', key: 'fixed_assets', indent: true, extKeys: ['Intangible_Assets_Under_Development', 'intangible_assets_under_development'] },
    { label: 'Total Current Assets', key: 'total_current_assets', indent: true },
    { label: 'Cash & Equivalents', key: 'cash_and_equivalents', indent: true },
    { label: 'Other Bank Balances', key: 'cash_and_equivalents', indent: true, extKeys: ['Other_Bank_Balances', 'other_bank_balances'] },
    { label: 'Current Investments', key: 'current_investments', indent: true },
    { label: 'Assets Held for Sale', key: 'total_assets', indent: true, extKeys: ['Assets_Held_For_Sale', 'assets_held_for_sale'] },
    { label: 'Total Assets', key: 'total_assets', bold: true },
    { label: '', key: 'total_assets', separator: true },
    { label: 'LIABILITIES & EQUITY', key: 'total_liabilities', sectionHeader: true },
    { label: 'Total Current Liabilities', key: 'total_current_liabilities', indent: true },
    { label: 'Trade Payables', key: 'trade_payables', indent: true },
    { label: 'Total Non-Current Liabilities', key: 'total_non_current_liabilities', indent: true },
    { label: 'Total Liabilities', key: 'total_liabilities', bold: true },
    { label: '', key: 'total_liabilities', separator: true },
    { label: 'Shareholders\' Funds / Equity', key: 'shareholders_funds', bold: true },
    { label: 'Other Equity', key: 'shareholders_funds', indent: true, extKeys: ['Other_Equity', 'other_equity'] },
    { label: 'Minority Interest', key: 'shareholders_funds', indent: true, extKeys: ['Minority_Interest', 'minority_interest'] },
    { label: 'Total Debt', key: 'total_debt', indent: true },
    { label: 'Net Debt', key: 'net_debt', indent: true },
    { label: '', key: 'invested_capital', separator: true },
    { label: 'Invested Capital', key: 'invested_capital', bold: true },
  ];

  const valueOf = (d: UnifiedFinancialRecord, row: RowDef): number | null => {
    if (row.extKeys) return resolveRecordValue(d, ...row.extKeys);
    const v = d[row.key];
    return typeof v === 'number' && !isNaN(v) ? v : null;
  };

  const fmt = (val: number | null | undefined) => {
    if (val == null || isNaN(val as number)) return '—';
    return formatCompactCurrency(val as number, region);
  };

  return (
    <Card>
      <CardHeader className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-neutral-800">Balance Sheet</h3>
        {unitLabel && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 whitespace-nowrap">
            {unitLabel}
          </span>
        )}
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                <th className="text-left py-2.5 px-3 text-neutral-500 font-semibold text-[11px] uppercase tracking-wider min-w-[12rem] sticky left-0 bg-neutral-50 z-10">
                  Line Item
                </th>
                {years.map((d) => (
                  <th
                    key={d.year}
                    className="text-right py-2.5 px-3 text-neutral-700 font-bold text-[11px] whitespace-nowrap min-w-[5rem]"
                  >
                    {formatFiscalYear(d.year, region)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                if (row.separator) {
                  return (
                    <tr key={idx}>
                      <td colSpan={years.length + 1} className="py-0.5 bg-neutral-100/70" />
                    </tr>
                  );
                }
                if (row.sectionHeader) {
                  return (
                    <tr key={idx} className="bg-primary-50/60">
                      <td
                        colSpan={years.length + 1}
                        className="py-2 px-3 text-[11px] font-bold text-primary-700 uppercase tracking-wider border-l-2 border-primary-400 sticky left-0 z-10 bg-primary-50"
                      >
                        {row.label}
                      </td>
                    </tr>
                  );
                }
                const hasAnyValue = years.some((d) => valueOf(d, row) != null);
                if (!hasAnyValue) return null;
                const rowBg = row.bold ? 'bg-neutral-50/80' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/30';
                const labelBg = row.bold ? 'bg-neutral-50/95' : idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60';
                return (
                  <tr
                    key={idx}
                    className={`border-b border-neutral-100 transition-colors hover:bg-violet-50/40 ${rowBg}`}
                  >
                    <td
                      className={`py-2 px-3 text-xs leading-snug sticky left-0 z-10 ${labelBg} ${
                        row.bold ? 'font-semibold text-neutral-800' : 'text-neutral-600'
                      } ${row.indent ? 'pl-7' : ''}`}
                    >
                      {row.label}
                    </td>
                    {years.map((d) => {
                      const val = valueOf(d, row);
                      const formatted = fmt(val);
                      const isNeg = typeof val === 'number' && val < 0;
                      return (
                        <td
                          key={d.year}
                          className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                            isNeg ? 'text-red-600' : row.bold ? 'font-semibold text-neutral-900' : 'text-neutral-700'
                          } ${row.bold ? 'font-semibold' : ''}`}
                        >
                          {formatted}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
